export type ReportCategory = "projects" | "finance" | "people" | "work";

export interface ReportConfig {
  id: string;
  title: string;
  description: string;
  category: ReportCategory;
  icon: string;
  color: "blue" | "green" | "purple" | "amber" | "red";
  endpoint: string;
}

export const REPORTS: ReportConfig[] = [
  {
    id: "project-health",
    title: "Project Health Summary",
    description:
      "Overview of all active projects with progress, tasks, and budget status.",
    category: "projects",
    icon: "Activity",
    color: "blue",
    endpoint: "/reports/project-health",
  },
  {
    id: "overdue-items",
    title: "Overdue Items",
    description:
      "All overdue tasks, actions, and milestones across projects.",
    category: "projects",
    icon: "AlertCircle",
    color: "red",
    endpoint: "/reports/overdue-items",
  },
  {
    id: "project-timeline",
    title: "Project Timeline",
    description: "Visual timeline of project phases and milestones.",
    category: "projects",
    icon: "Calendar",
    color: "blue",
    endpoint: "/reports/project-timeline",
  },
  {
    id: "budget-vs-actual",
    title: "Budget vs Actual",
    description: "Compare allocated budgets against actual expenses.",
    category: "finance",
    icon: "TrendingUp",
    color: "green",
    endpoint: "/reports/budget-vs-actual",
  },
  {
    id: "expense-analysis",
    title: "Expense Analysis",
    description:
      "Breakdown of expenses by category, project, and time period.",
    category: "finance",
    icon: "PieChart",
    color: "green",
    endpoint: "/reports/expense-analysis",
  },
  {
    id: "profitability",
    title: "Profitability Report",
    description:
      "Project margins comparing estimated costs vs quoted prices.",
    category: "finance",
    icon: "DollarSign",
    color: "green",
    endpoint: "/reports/profitability",
  },
  {
    id: "team-workload",
    title: "Team Workload",
    description:
      "Who is working on what and how loaded each team member is.",
    category: "people",
    icon: "Users",
    color: "purple",
    endpoint: "/reports/team-workload",
  },
  {
    id: "activity-summary",
    title: "Activity Summary",
    description: "What each team member has accomplished in a period.",
    category: "people",
    icon: "Activity",
    color: "purple",
    endpoint: "/reports/activity-summary",
  },
  {
    id: "meeting-attendance",
    title: "Meeting Attendance",
    description:
      "Meeting attendance rates and engagement by team member.",
    category: "people",
    icon: "UserCheck",
    color: "purple",
    endpoint: "/reports/meeting-attendance",
  },
  {
    id: "tasks-status",
    title: "Tasks Status Report",
    description: "Task completion rates, trends, and breakdown by status.",
    category: "work",
    icon: "CheckSquare",
    color: "amber",
    endpoint: "/reports/tasks-status",
  },
  {
    id: "actions-tracker",
    title: "Actions Tracker",
    description:
      "Action completion rates, sources, and overdue by assignee.",
    category: "work",
    icon: "Zap",
    color: "amber",
    endpoint: "/reports/actions-tracker",
  },
  {
    id: "decisions-log",
    title: "Decisions Log",
    description: "Chronological record of all decisions made.",
    category: "work",
    icon: "Scale",
    color: "amber",
    endpoint: "/reports/decisions-log",
  },
  {
    id: "ideas-pipeline",
    title: "Ideas Pipeline",
    description: "Ideas funnel from submission through approval.",
    category: "work",
    icon: "Lightbulb",
    color: "amber",
    endpoint: "/reports/ideas-pipeline",
  },
];

export const COLOR_PALETTE = {
  blue: { bg: "hsl(210,100%,96%)", fg: "hsl(210,100%,40%)", solid: "hsl(210,100%,50%)" },
  green: { bg: "hsl(142,72%,94%)", fg: "hsl(142,72%,32%)", solid: "hsl(142,72%,40%)" },
  purple: { bg: "hsl(270,75%,95%)", fg: "hsl(270,75%,42%)", solid: "hsl(270,75%,55%)" },
  amber: { bg: "hsl(38,92%,94%)", fg: "hsl(38,92%,38%)", solid: "hsl(38,92%,50%)" },
  red: { bg: "hsl(0,84%,95%)", fg: "hsl(0,72%,45%)", solid: "hsl(0,72%,55%)" },
} as const;

export const CHART_PALETTE = [
  "hsl(210,100%,50%)",
  "hsl(142,72%,40%)",
  "hsl(270,75%,55%)",
  "hsl(38,92%,50%)",
  "hsl(0,72%,55%)",
  "hsl(180,55%,40%)",
  "hsl(330,75%,55%)",
  "hsl(60,80%,45%)",
];

export interface CommonReportFilters {
  dateFrom?: string;
  dateTo?: string;
  companyId?: string;
  productId?: string;
  projectId?: string;
  status?: string;
  category?: string;
  assignedToId?: string;
  userId?: string;
  source?: string;
  fiscalYear?: number;
  priority?: string;
  severity?: string;
  type?: string;
  currency?: string;
}

export function defaultFilters(): CommonReportFilters {
  const to = new Date();
  const from = new Date();
  from.setDate(from.getDate() - 30);
  return {
    dateFrom: from.toISOString().slice(0, 10),
    dateTo: to.toISOString().slice(0, 10),
  };
}
